import { createGlobalStyle, css } from 'styled-components';
import { toObject } from '@wojtekmaj/color-utils';

import gradients from '@rewardopl/styles/gradients';
import shadows from '@rewardopl/styles/shadows';
import variables from '@rewardopl/styles/variables';

import { Button } from '@rewardopl/react-ui/button/index.styles';
import { Link } from '@rewardopl/react-ui/link_button/index.styles';

import montserratWoff2 from '../static/fonts/montserrat.woff2';
import montserratItalicWoff2 from '../static/fonts/montserrat_italic.woff2';

const fonts = [
  {
    fontFamily: 'Montserrat',
    fontStyle: 'normal',
    fontWeight: '100 900',
    src: montserratWoff2,
  },
  {
    fontFamily: 'Montserrat',
    fontStyle: 'italic',
    fontWeight: '100 900',
    src: montserratItalicWoff2,
  },
];

const fontStyle = css`
  ${fonts.map(
    (font) => css`
      @font-face {
        font-family: ${font.fontFamily};
        font-display: swap;
        src:
          url(${font.src}) format('woff2 supports variations'),
          url(${font.src}) format('woff2-variations');
        font-style: ${font.fontStyle};
        font-weight: ${font.fontWeight};
      }
    `,
  )}
`;

const style = document.createElement('style');
style.innerHTML = fontStyle.join('');
document.head.appendChild(style);

export const linkStyle = css`
  color: var(--link);
  font: inherit;
  text-decoration: none;
  cursor: pointer;

  &:hover,
  &:focus {
    text-decoration: underline;
  }

  &.primary {
    font: inherit;
    font-weight: 700;

    &:hover,
    &:focus {
      color: var(--primary-dark);
      text-decoration: none;
    }
  }
`;

function addUnit(name: string, value: string | number): string | number {
  if (!name.endsWith('-opacity') && typeof value === 'number') {
    return `${value}px`;
  }

  return value;
}

const vars = css`
  ${(props) =>
    Object.entries({
      ...props.theme.colors,
      ...gradients,
      ...shadows,
    }).map(([name, value]) => `--${name}: ${value};`)}
`;

const unitVars = css`
  ${Object.entries(variables).map(([name, value]) => `--${name}: ${addUnit(name, value)};`)}
`;

function toColorsRgb<T extends Record<string, string>>(colors: T) {
  const colorsRgb = Object.entries(colors).reduce(
    (obj, [name, value]) => {
      try {
        const { r, g, b } = toObject(value);

        obj[name as keyof T] = `${r}, ${g}, ${b}`;
      } catch {
        // Do nothing - some colors can't be converted to RGB
      }

      return obj;
    },
    {} as Record<keyof T, string>,
  );

  return colorsRgb;
}

const rgbVars = css`
  ${(props) => Object.entries(toColorsRgb(props.theme.colors)).map(([name, value]) => `--${name}-rgb: ${value};`)}
`;

export const GlobalStyle = createGlobalStyle`
  :root {
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    font-smoothing: antialiased;
    -webkit-text-size-adjust: none;
    -moz-text-size-adjust: none;
    text-size-adjust: none;
    -webkit-tap-highlight-color: transparent;

    ${vars}
    ${unitVars}
    ${rgbVars}
  }

  body {
    min-height: 100vh;
    min-height: 100dvh;
    line-height: 1.333;
    margin: 0;
    overflow: hidden;
    background: var(--background);
    color: var(--text);
    font-family: Montserrat, sans-serif;
    font-weight: 600;
    font-size: 14px;
  }

  * {
    -webkit-user-select: none;
    user-select: none;
  }

  *,
  *:before,
  *:after {
    box-sizing: border-box;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin-top: 0;
    font-weight: 700;
  }

  h1 {
    font-size: 28px;
  }

  h2 {
    font-size: 20px;
  }

  h3 {
    font-size: 16px;
  }

  a:not([class*="sc-"]) {
    ${linkStyle}
  }

  ul,
  ol {
    margin: 0;
    padding: 0;
    list-style-position: inside;
  }

  button {
    margin: 0;
    cursor: pointer;
  }

  ${Link}, ${Button} {
    font-weight: 700;
    text-transform: uppercase;
  }

  hr {
    width: 100%;
    border: 0;
    border-top: 1px solid gray;
  }

  label {
    display: block;
  }

  table {
    border-collapse: separate;
    border-spacing: 24px 10px;
    margin: -10px -24px;

    th {
      margin-bottom: 4px;
      text-align: left;
      font-weight: 600;
      vertical-align: top;

      @media screen and (min-width: 769px) {
        margin-bottom: 0;
      }
    }

    th,
    td {
      display: block;

      @media screen and (min-width: 769px) {
        display: table-cell;
      }
    }

    tr:not(:first-child) {
      th {
        margin-top: 10px;

        @media screen and (min-width: 769px) {
          margin-top: 0;
        }
      }
    }
  }
`;
